import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import _debounce from 'lodash.debounce'
import {useForm, Controller} from 'react-hook-form';

import styles from './ModuleDestinationSearch.module.scss'
import ModuleDropdown from '../../../../components/ModuleDropdown';
import FieldInput from '../../../../components/FieldInput';
import localStorage from 'js/helpers/localStorage';
import {
  pushClearSearch,
  pushCloseSearch,
  pushSearchClick,
  pushTopDestinationsClick,
  pushSearchAutocomplete,
} from 'js/helpers/gtm'
import Loader from '../Loader';
import {searchBox} from '../../../../drupalSettings';
import fetchCityHotelsAutocomplete from 'js/apiCalls/cityHotelsAutocomplete';
import {
  LATEST_SEARCH_LOCAL_STORAGE,
  LATEST_SEARCH_LIMIT,
  KEYWORD_SEARCH_LENGTH,
} from '../../../../constants'
import Suggestions from '../Suggestions';

const cx = classnames.bind(styles);
const SEARCH_INPUT = 'destination';
const {
  yourLatestSearch,
  ourAparthotelIn,
  ourBestDestinations,
  wantToLiveIn,
  previewLabel,
} = searchBox?.titles || {};

const ModuleDestinationSearch = function ({onClose, onSubmit, value, onUpdate}) {
  const {
    handleSubmit,
    control,
    watch,
  } = useForm({
    defaultValues: {
      [SEARCH_INPUT]: value,
    }
  });
  const [latestSearch, setLatestSearch] = useState(null);
  const [cityHotels, setCityHotels] = useState(null);
  const [geoPlaces, setGeoPlaces] = useState(null);
  const [destinationItems, setDestinationItems] = useState(null);
  const [loading, setLoading] = useState(false)
  const destinationValue = watch(SEARCH_INPUT);

  const onModalClose = function () {
    // Update search value on modal close (if it's different from current value)
    if (destinationValue !== value) {
      onUpdate({name: destinationValue})
    }

    pushCloseSearch()
    onClose && onClose()
  }

  const fetchAutocompleteData = _debounce(async function (val) {
    const HOTEL_SOURCE = 'hod_hotels'
    const GEO_SOURCE = 'geomaj'
    const {data} = await fetchCityHotelsAutocomplete(val)
    const hotelsResult = data?.predictions
      ?.filter(el => el.source === HOTEL_SOURCE)
      ?.map(el => ({...el, name: el.description}))
    const geoResult = data?.predictions
      ?.filter(el => el.source === GEO_SOURCE)
      ?.map(el => ({...el, name: el.description}))
    setCityHotels(hotelsResult);
    setGeoPlaces(geoResult);
    setLoading(false)
  }, 300)

  const onSearchQueryUpdate = function (data) {
    const val = data.trim()
    if (val.length >= KEYWORD_SEARCH_LENGTH) {
      setLoading(true)
      fetchAutocompleteData(val)
    } else {
      setCityHotels(null);
      setGeoPlaces(null);
    }
  }

  const onFormSubmit = function ({destination}) {
    saveLatestSearch(destination)
    pushSearchClick(false)
    onSubmit({name: destination})
  }

  const onDestinationClick = function (item) {
    if (item.source === 'geomaj') {
      pushSearchAutocomplete(item.name)
    } else {
      pushTopDestinationsClick(item.nameEn || item.name)
    }
    onSubmit(item)
  }

  const fetchLatestSearch = function () {
    const data = localStorage.get(LATEST_SEARCH_LOCAL_STORAGE) || [];
    setLatestSearch(data)
  }

  const saveLatestSearch = function (txt, {source, type, id} = {}) {
    const data = localStorage.get(LATEST_SEARCH_LOCAL_STORAGE) || [];
    if (data.some(el => el.name === txt)) {
      return
    }
    const newData = [
      {name: txt, source, type, id},
      ...data
    ].slice(0, LATEST_SEARCH_LIMIT)
    localStorage.set(LATEST_SEARCH_LOCAL_STORAGE, newData)
  }

  const onLatestSearchClear = function () {
    localStorage.remove(LATEST_SEARCH_LOCAL_STORAGE)
    setLatestSearch(null)
  }

  const updateDestinations = function () {
    setDestinationItems(searchBox?.destinations || [])
  }

  const onDestinationClear = function () {
    pushClearSearch();
    onUpdate({name: ''});
  }

  useEffect(() => {
    fetchLatestSearch()
    updateDestinations()
  }, []);

  useEffect(() => {
    onSearchQueryUpdate(destinationValue)
  }, [destinationValue]);

  return (
    <ModuleDropdown
      autoFocus={false}
      title={wantToLiveIn}
      onClose={onModalClose}
    >
      <div className={cx('wrapper')}>
        <form className={cx('search')} onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            control={control}
            name={SEARCH_INPUT}
            rules={{ minLength: KEYWORD_SEARCH_LENGTH }}
            render={({field}) => (
              <FieldInput
                autoFocus
                onClear={onDestinationClear}
                placeholder={previewLabel}
                clearable
                {...field}
              />
            )}
          />
        </form>
        <div className={cx('suggestions')}>
          {loading && (<Loader />)}
          {!loading && (
            <>
              {destinationValue.length <= KEYWORD_SEARCH_LENGTH && (
                <>
                  {!!latestSearch?.length && (
                    <Suggestions
                      data={latestSearch}
                      itemsIcon="ZOOM"
                      titleIcon="GEOLOC"
                      onClick={onDestinationClick}
                      onClear={onLatestSearchClear}
                      title={yourLatestSearch}
                    />
                  )}
                  {!!destinationItems?.length && (
                    <Suggestions
                      data={destinationItems}
                      titleIcon="E_WLCOUT"
                      onClick={onDestinationClick}
                      title={ourBestDestinations}
                    />
                  )}
                </>
              )}
              {destinationValue.length >= KEYWORD_SEARCH_LENGTH && (
                <>
                  {!!geoPlaces?.length && (
                    <Suggestions
                      data={geoPlaces}
                      itemsIcon="GEOLOC"
                      titleIcon="GEOLOC"
                      onClick={onDestinationClick}
                    />
                  )}
                  {!!cityHotels?.length && (
                    <Suggestions
                      data={cityHotels}
                      itemsIcon="HOME_HEART"
                      onClick={onDestinationClick}
                      title={ourAparthotelIn}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ModuleDropdown>
  )
}

ModuleDestinationSearch.propTypes = {
  data: PropTypes.any
}

export default ModuleDestinationSearch;
